import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StatusCode } from "../../../components/Alert.tsx";
import { FormActions } from "../../../components/form/index.js";
import { useAlert } from "../../../contexts/alertProvider.tsx";
import { useJournalContext } from "../../../contexts/journal.tsx";
import { useUsersContext } from "../../../contexts/users.tsx";
import { JournalName } from "../../../models/JournalName.ts";
import { Journal } from "../../../models/journals/journal.ts";
import { JournalSearchParam } from "../../../models/searchParams/journalSearchParam.ts";
import { JournalGroup } from "./journalGroup.tsx";

export const JournalEdit = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { dateTime } = useParams();
    const { groupedJournals, setSearchParam, insertJournals, updateJournals, deleteJournals } = useJournalContext();
    const { journalNames } = useUsersContext();
    useEffect(() => {
        setSearchParam((prevData: JournalSearchParam) => ({ ...prevData, dateTime: new Date(dateTime as string) }));
    }, [dateTime]);
    const [localGroupedJournals, setLocalGroupedJournals] = useState<Journal[][]>(groupedJournals);
    useEffect(() => {
        setLocalGroupedJournals(groupedJournals);
    }, [groupedJournals])
    groupedJournals.forEach(group => {
        if (!journalNames.find(jn => jn.name == group[0]?.name))
            journalNames.push({ name: group[0]?.name } as JournalName);
    });
    const [selectedJournalName, setSelectedJournalName] = useState(journalNames[0]);
    useEffect(() => {
        if (journalNames)
            setSelectedJournalName(journalNames[0])
    }, [journalNames])
    const handleJournalNameChange = (e: any) => {
        const { value } = e.target;
        setSelectedJournalName(journalNames.find(jn => jn.name == value) ?? journalNames[0]);
    }

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        const [type, id] = name.split('-');

        if (!['Aliment', 'Recipe', 'Meal'].includes(type)) { showAlert(StatusCode.warning, "Type not found"); return; }

        const updatedLocalGroupedJournals = [...localGroupedJournals];

        const groupIndex = updatedLocalGroupedJournals.findIndex(group => group[0].name == selectedJournalName.name);

        if (groupIndex === -1) {
            showAlert(StatusCode.warning, "Group not found");
            return;
        }

        const group = [...updatedLocalGroupedJournals[groupIndex]];

        const journalIndex = group.findIndex(journal => journal[`${type.toLowerCase()}ID`] == id);

        if (journalIndex === -1) {
            showAlert(StatusCode.warning, "Record not found");
            return;
        }
        const journal = { ...group[journalIndex] };
        journal.amount = value;
        group[journalIndex] = journal;
        updatedLocalGroupedJournals[groupIndex] = group;

        setLocalGroupedJournals(updatedLocalGroupedJournals);
    };

    const handleAddItem = (entity: any) => {
        var group: Journal[] = localGroupedJournals.find(group => group[0].name == selectedJournalName.name) ?? [];

        const existingItem = group.find(journal => journal.alimentID == entity.resultID || journal.recipeID == entity.resultID || journal.mealID == entity.resultID);
        if (existingItem) { showAlert(StatusCode.warning, "Item already exists"); return; }

        var newItem: Journal = { journalID: 0, name: selectedJournalName.name, amount: entity.defaultQuantity, alimentID: null, recipeID: null, mealID: null, dateTime: new Date(dateTime?.toString() ?? '') } as Journal;
        if (entity.type == "Aliment") {
            newItem.alimentID = entity.resultID;
            newItem.aliment = entity;
        }
        if (entity.type == "Recipe") {
            newItem.recipeID = entity.resultID;
            newItem.recipe = entity;
        }
        if (entity.type == "Meal") {
            newItem.mealID = entity.resultID;
            newItem.meal = entity.meal;
        }

        group.push(newItem);

        setLocalGroupedJournals(prevData => {
            const updatedData = [...prevData];

            const groupIndex = updatedData.findIndex(g => g[0].name == selectedJournalName.name);
            if (groupIndex !== -1) {
                updatedData[groupIndex] = group;
            } else {
                updatedData.push(group);
            }

            return updatedData;
        });
    }

    const handleDeleteItem = (entity: Journal) => {
        const updatedLocalGroupedJournals = [...localGroupedJournals];
        const groupIndex = updatedLocalGroupedJournals.findIndex(group => group[0].name == selectedJournalName.name);

        if (groupIndex === -1) {
            showAlert(StatusCode.warning, "Group not found");
            return;
        }

        const group = [...updatedLocalGroupedJournals[groupIndex]];

        const journalIndex = group.findIndex(journal => journal == entity);
        if (journalIndex !== -1) {
            group.splice(journalIndex, 1);
        }

        updatedLocalGroupedJournals[groupIndex] = group;

        setLocalGroupedJournals(updatedLocalGroupedJournals);
    };

    const handleSubmit = () => {
        const newJournals: Journal[] = [];
        const updatedJournals: Journal[] = [];
        const deletedJournals: Journal[] = [];

        localGroupedJournals.forEach(group => {
            group.forEach((journal: Journal) => {
                if (journal.journalID == 0) {
                    newJournals.push(journal);
                } else {
                    const existingJournal = findExistingJournal(journal.journalID);
                    if (existingJournal && !areJournalsEqual(existingJournal, journal)) {
                        updatedJournals.push(journal);
                    }
                }
            });
        });

        groupedJournals.forEach(group => {
            group.forEach((journal: Journal) => {
                const isDeleted = !localGroupedJournals.some(localGroup =>
                    localGroup.some(localJournal => localJournal.journalID == journal.journalID)
                );

                if (isDeleted) {
                    deletedJournals.push(journal);
                }
            });
        });

        var success = true;
        success = success && insertJournals(newJournals);
        success = success && updateJournals(updatedJournals);
        success = success && deleteJournals(deletedJournals);

        if (success)
            navigate(-1);
    };

    const findExistingJournal = (journalID: number) => {
        for (let group of groupedJournals) {
            const journal = group.find(j => j.journalID == journalID);
            if (journal) {
                return journal;
            }
        }
        return null;
    };

    const areJournalsEqual = (existingJournal: Journal, newJournal: Journal) => {
        return existingJournal.amount == newJournal.amount &&
            existingJournal.alimentID == newJournal.alimentID &&
            existingJournal.recipeID == newJournal.recipeID &&
            existingJournal.mealID == newJournal.mealID;
    };

    if (!selectedJournalName)
        return (<></>);

    return (
        <div>
            <div className="form-floating mb-3">
                <select
                    className="form-select"
                    id="selectedJournalName"
                    value={selectedJournalName.name}
                    onChange={handleJournalNameChange}
                >
                    {
                        journalNames.map((option) => {
                            var length = (localGroupedJournals.find(group => group[0]?.name == option.name) ?? []).length;
                            return (
                                <option key={option.name} value={option.name}>
                                    {option.name}
                                    {
                                        length > 0 &&
                                        <> ({length} item{length > 1 && <>s</>})</>
                                    }
                                </option>
                            )
                        })
                    }
                </select>
                <label htmlFor="floatingSelect">Meal</label>
            </div>

            <JournalGroup
                journalGroup={localGroupedJournals.find(group => group[0]?.name == selectedJournalName.name) ?? []}
                handleInputChange={handleInputChange}
                handleAddItem={handleAddItem}
                handleDeleteItem={handleDeleteItem} />
            <FormActions
                mainAction={handleSubmit}
                mainActionText="Save"
                showCancelButton />
        </div >
    );
}